import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/http";
import { postFoodLog } from "@/lib/offlineMeals";
import { readDashCache, writeDashCache } from "@/lib/dashCache";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { useEnabledFeatures } from "@/hooks/useEnabledFeatures";
import Layout from "@/components/Layout";
import PageBackground from "@/components/PageBackground";
import QuickTrackMenu from "@/components/QuickTrackMenu";
import { ProWelcomeTour } from "@/components/ProWelcomeTour";
import AiQuotaChip from "@/components/AiQuotaChip";
import ProStatusBadge from "@/components/ProStatusBadge";
import { autoSyncNativeHealthIfDue, isNativeHealthSupported, syncNativeHealth } from "@/lib/healthkit";
import { drainPendingWidgetWater, refreshWidgetFromServer } from "@/lib/widgetSync";
import { registerPullRefresh } from "@/lib/pullRefresh";
import DashboardScoresRow from "@/components/DashboardScoresRow";
import PlateTodayCard from "@/components/PlateTodayCard";
import GamificationWidget from "@/components/GamificationWidget";
import DailySummaryCard from "@/components/DailySummaryCard";
import MacroCalculatorDialog from "@/components/MacroCalculatorDialog";
import NutritionPlanDialog from "@/components/NutritionPlanDialog";
import GoalReviewCard from "@/components/GoalReviewCard";
import FoodSearchDialog from "@/components/FoodSearchDialog";
import VoiceFoodButton from "@/components/VoiceFoodButton";
import VoiceMultiSheet from "@/components/VoiceMultiSheet";
import OnboardingWizard from "@/components/OnboardingWizard";
import Mascot from "@/components/Mascot";
import DailyCheckinCard from "@/components/DailyCheckinCard";
import WaterMilestone2LOverlay from "@/components/WaterMilestone2LOverlay";
import TrainingCompleteOverlay from "@/components/TrainingCompleteOverlay";
import StepsGoalOverlay from "@/components/StepsGoalOverlay";
import SleepGoalOverlay from "@/components/SleepGoalOverlay";
import { defaultMealTypeForNow, suggestOtherMeal } from "@/lib/mealTime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Zap, Footprints, Droplet, ChevronLeft, ChevronRight, ChevronDown,
  Plus, Sparkles, BookOpen, ShoppingBag, Utensils, Scale, TrendingDown,
  Wand2, Camera, Mic, X, Pencil, Calculator, Info, Refrigerator, Dumbbell, Flame, ChefHat, CalendarDays, Undo2, Settings2, Target,
} from "lucide-react";

const GLASS_ML = 250;
const ML_PER_LITER = 1000;

function todayIso() {
  // Use LOCAL date (not UTC) so users in CEST/CET see the correct "today"
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function shiftIso(dateStr, days) {
  const d = new Date(dateStr); d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/* ---------- Calorie Ring (SVG) ---------- */
function CalorieRing({ consumed, target }) {
  const { t } = useT();
  const size = 180;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const safeTarget = target || 0;
  const over = consumed > safeTarget;
  const remaining = Math.max(0, safeTarget - consumed);
  const ratio = safeTarget ? consumed / safeTarget : 0;
  // Ring fills = REMAINING calories. Starts full (100%), empties as you eat.
  const pct = safeTarget ? (over ? 1 : remaining / safeTarget) : 0;
  // Unified color logic (matches DailySummaryCard KCAL cell):
  //   consumed > target        → RED (over budget)
  //   consumed < 70% of target → RED (too far under — user requested)
  //   70% ≤ consumed ≤ target  → GREEN (safe zone)
  //   no target set            → neutral teal (base tone)
  const tooLow = safeTarget > 0 && ratio < 0.7;
  const danger = safeTarget > 0 && (over || tooLow);
  const grad = danger
    ? { from: "#EF4444", to: "#B91C1C", text: "#B91C1C", label: "#B91C1C" }
    : safeTarget > 0
      ? { from: "#22C55E", to: "#15803D", text: "#166534", label: "#15803D" }
      : { from: "#7CB4B7", to: "#2C5254", text: "#1A1D1E", label: "#5A8B8F" };
  // Animate stroke from 0 (empty) → target pct on first render.
  const [animPct, setAnimPct] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimPct(pct));
    return () => cancelAnimationFrame(id);
  }, [pct]);
  // Count-up animation for the displayed number.
  const targetValue = safeTarget ? (over ? consumed - safeTarget : remaining) : consumed;
  const [displayValue, setDisplayValue] = useState(0);
  // Small "pop" when the underlying target value changes (kcal added).
  const [popKey, setPopKey] = useState(0);
  const firstRunRef = useRef(true);
  useEffect(() => {
    if (firstRunRef.current) { firstRunRef.current = false; }
    else { setPopKey((k) => k + 1); }
  }, [targetValue]);
  useEffect(() => {
    if (targetValue === 0) { setDisplayValue(0); return; }
    const start = performance.now();
    const duration = 900;
    const startVal = displayValue;
    let raf;
    const step = (ts) => {
      const p = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayValue(Math.round(startVal + (targetValue - startVal) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetValue]);

  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        {/* Track — subtle */}
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#EEF1F2" strokeWidth={stroke} fill="none" />
        {/* Gradient stop for a premium finish */}
        <defs>
          <linearGradient id="cal-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={grad.from} />
            <stop offset="100%" stopColor={grad.to} />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="url(#cal-grad)"
          strokeWidth={stroke} strokeLinecap="round" fill="none"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - animPct)}
          style={{ transition: "stroke-dashoffset 1200ms cubic-bezier(0.4, 0, 0.2, 1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        {over && (
          <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: grad.label }}>{t("cliDash.kcalOver")}</span>
        )}
        {!over && tooLow && (
          <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: grad.label }}>{t("cliDash.kcalTooLow")}</span>
        )}
        <span
          key={popKey}
          className="font-heading font-bold leading-none tabular-nums animate-[numberPop_450ms_ease-out]"
          style={{ fontSize: 44, color: grad.text }}
          data-testid="cal-remaining"
        >
          {displayValue}
        </span>
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mt-1">
          kcal
        </span>
      </div>
    </div>
  );
}

/* ---------- Next Meal Suggestion helper ---------- */
// Returns { key, ratio } for the next meal based on hour, or null if the day is done.
// Ratios are % of the daily kcal budget dedicated to each remaining meal.
function pickNextMeal(hour, loggedByMeal, t) {
  // Typical daily split: colazione 25% · spuntino 10% · pranzo 30% · merenda 10% · cena 25%
  const schedule = [
    { key: "colazione", end: 11, ratio: 0.25, labelKey: "diary.breakfast" },
    { key: "spuntino",  end: 12, ratio: 0.10, labelKey: "diary.snack" },
    { key: "pranzo",    end: 15, ratio: 0.30, labelKey: "diary.lunch" },
    { key: "merenda",   end: 18, ratio: 0.10, labelKey: "diary.afternoon" },
    { key: "cena",      end: 23, ratio: 0.25, labelKey: "diary.dinner" },
  ];
  const next = schedule.find((s) => hour < s.end && !loggedByMeal[s.key]);
  if (!next) return null;
  return { key: next.key, ratio: next.ratio, label: t(next.labelKey) };
}

/* ---------- Macro Bar Row ---------- */
function MacroBar({ label, value, target, accent }) {
  const pct = target > 0 ? Math.min(100, (value / target) * 100) : 0;
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-center gap-1.5">
        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: accent }} aria-hidden />
        <p className="text-sm font-medium text-slate-700">{label}</p>
      </div>
      <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-1000 ease-out"
          style={{ width: `${pct}%`, background: accent }}
        />
      </div>
      <p className="mt-2 text-xs text-center">
        <span className="font-semibold tabular-nums" style={{ color: accent }}>{Math.round(value)}g</span>
        <span className="text-slate-400"> / {Math.round(target || 0)}g</span>
      </p>
    </div>
  );
}

/* ---------- Fitness Card (right of ring) ---------- */
function FitnessCard({ t, kcalBurned, steps, waterL, targetL, addWater, workoutMin, workoutCount, workoutKcal, streak, date, profile, saveWaterSettings, foodWaterMl }) {
  const streakDays = Number(streak?.current || 0);
  const showStreak = streakDays > 0;
  // Inline custom-ml editor toggled by the "Custom" button. Kept minimal
  // (numeric input + confirm) so the client can add a bottle size that
  // isn't 250/500 ml without opening a modal.
  const [customOpen, setCustomOpen] = useState(false);
  const [customMl, setCustomMl] = useState("");
  // Info popover explaining where "kcal verbrannt" comes from. Sofia's
  // clients ask this weekly — inline collapsible copy is cheaper than
  // a Support conversation.
  const [kcalInfoOpen, setKcalInfoOpen] = useState(false);
  // Per-day stack of exact ml increments so Rückgängig removes the LAST
  // amount that was added (250, 300, 500 …) instead of a fixed glass.
  // Feb 2026 bug: adding 300 ml then Undoing only removed 250 ml because
  // `addWater(-1)` was a plain decrement in "glasses" units.
  const waterHistoryKey = `water_history_${date}`;
  const [waterHistory, setWaterHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(waterHistoryKey) || "[]"); }
    catch { return []; }
  });
  // Floating "+250 ml" chip that briefly rises above the water button when
  // ANY amount is added (including Custom). Users kept missing the ripple
  // animation on the Custom path, hence this loud but tiny confirmation.
  const [waterFlyChip, setWaterFlyChip] = useState(null);
  const showWaterFly = (ml) => {
    setWaterFlyChip({ ml, id: Date.now() });
    setTimeout(() => setWaterFlyChip((c) => (c && c.id ? null : c)), 900);
  };
  const submitCustom = () => {
    const n = parseInt(customMl, 10);
    if (!Number.isFinite(n) || n <= 0 || n > 3000) return;
    // Push exact ml to history + convert to fractional glasses for state.
    pushWater(n);
    setCustomMl("");
    setCustomOpen(false);
  };
  // Add a specific amount (in ml) — used by the +250 / +500 quick buttons
  // and the Custom submit. Records the exact ml in the undo stack.
  const pushWater = (ml) => {
    if (!ml || ml <= 0) return;
    showWaterFly(ml);
    try { navigator.vibrate?.(12); } catch { /* noop */ }
    const nextHistory = [...waterHistory, { ml, ts: Date.now() }].slice(-40);
    setWaterHistory(nextHistory);
    try { localStorage.setItem(waterHistoryKey, JSON.stringify(nextHistory)); } catch { /* noop */ }
    addWater(ml / GLASS_ML);
  };
  const undoWater = () => {
    if (!waterHistory.length) {
      // Empty stack (older log without history) → fall back to a single-glass
      // decrement so the button is never a no-op for legacy days.
      try { navigator.vibrate?.(6); } catch { /* noop */ }
      addWater(-1);
      return;
    }
    const last = waterHistory[waterHistory.length - 1];
    const nextHistory = waterHistory.slice(0, -1);
    setWaterHistory(nextHistory);
    try { localStorage.setItem(waterHistoryKey, JSON.stringify(nextHistory)); } catch { /* noop */ }
    try { navigator.vibrate?.(6); } catch { /* noop */ }
    addWater(-last.ml / GLASS_ML);
  };
  // ---- Personal containers + editable daily target ----
  const containers = (profile?.water_containers || []).filter((c) => c && Number(c.ml) > 0).slice(0, 3);
  const [waterSettingsOpen, setWaterSettingsOpen] = useState(false);
  const [draftTarget, setDraftTarget] = useState("");
  const [draftContainers, setDraftContainers] = useState([]);
  const [savingWater, setSavingWater] = useState(false);
  const toggleWaterSettings = () => {
    if (!waterSettingsOpen) {
      setDraftTarget(String(targetL));
      setDraftContainers(containers.map((c) => ({ label: c.label || "", ml: String(c.ml) })));
    }
    setWaterSettingsOpen((v) => !v);
  };
  const saveWater = async () => {
    const tl = parseFloat(String(draftTarget).replace(",", "."));
    const list = draftContainers
      .map((c) => ({ label: (c.label || "").trim().slice(0, 24), ml: parseInt(c.ml, 10) }))
      .filter((c) => Number.isFinite(c.ml) && c.ml >= 50 && c.ml <= 2000);
    setSavingWater(true);
    try {
      await saveWaterSettings({
        ...(Number.isFinite(tl) && tl >= 0.5 && tl <= 8 ? { water_target_l: tl } : {}),
        water_containers: list,
      });
      setWaterSettingsOpen(false);
    } finally {
      setSavingWater(false);
    }
  };
  return (
    <div id="water-quick-card" data-testid="water-quick-card" className="rounded-3xl bg-white p-5 shadow-sm border border-slate-100 flex flex-col justify-between min-h-[220px] transition-shadow">
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-[#E8951C]/10 text-[#E8951C] flex items-center justify-center shrink-0">
            <Zap className="h-4 w-4" />
          </div>
          <p className="text-sm text-slate-700 flex items-center gap-1.5 flex-1 min-w-0">
            <span className="font-semibold text-slate-900">{kcalBurned}</span> {t("cliDash.kcalBurned")}
            <button
              type="button"
              onClick={() => setKcalInfoOpen((v) => !v)}
              data-testid="kcal-burned-info-btn"
              className="h-4 w-4 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition active:scale-90 shrink-0"
              aria-label="How kcal are calculated"
            >
              <Info className="h-2.5 w-2.5" />
            </button>
          </p>
        </div>
        {kcalInfoOpen && (
          <div
            className="rounded-2xl bg-gradient-to-br from-orange-50 to-amber-50 border border-amber-200 p-3 space-y-2 text-[11px] leading-relaxed animate-[fadeIn_0.15s_ease-out]"
            data-testid="kcal-burned-info-panel"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="font-bold text-amber-900 text-xs">
                {t("cliDash.kcalInfoTitle") || "Wie werden die kcal berechnet?"}
              </p>
              <button
                type="button"
                onClick={() => setKcalInfoOpen(false)}
                data-testid="kcal-burned-info-close"
                className="text-amber-700 hover:text-amber-900 -mt-0.5 -mr-1"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <p className="text-slate-700">
              <span className="font-semibold text-amber-900">{kcalBurned} kcal</span> = {t("cliDash.kcalInfoTotalHint")
                || "Alltagsbewegung heute (Apple Health Gesamtverbrauch minus Workout-kcal) — ohne Training."}
            </p>
            <p className="text-slate-700">
              <span className="font-semibold text-orange-700">🔥 {workoutKcal} kcal</span> = {t("cliDash.kcalInfoWorkoutHint")
                || "zusätzlich aus deinen geloggten Workouts (WHOOP + manuell, ±5 min dedupliziert)."}
            </p>
            <p className="text-[10px] text-slate-500 pt-1 border-t border-amber-200/60">
              {t("cliDash.kcalInfoFallback")
                || "Ohne Apple Health-Sync wird die Alltagsaktivität aus deinen Schritten geschätzt (~0,04 kcal pro Schritt)."}
            </p>
          </div>
        )}
        {(workoutCount > 0 || workoutMin > 0) && (
          <NavLink to="/app/allenamenti" data-testid="dashboard-workout-row" className="flex items-center gap-3 group">
            <div className="h-8 w-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
              <Dumbbell className="h-4 w-4" />
            </div>
            <p className="text-sm text-slate-700 group-hover:underline">
              <span className="font-semibold text-slate-900">{workoutCount}</span> {t("workouts.todayCount")} · <span className="font-semibold">{workoutMin}</span> min · <span className="text-orange-600">🔥 {workoutKcal}</span>
            </p>
          </NavLink>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="h-8 w-8 rounded-full bg-[#5A8B8F]/10 text-[#5A8B8F] flex items-center justify-center shrink-0">
            <Footprints className="h-4 w-4" />
          </div>
          <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">{Math.round(steps).toLocaleString()}</span> {t("cliDash.steps")}</p>
          {showStreak && (
            <span
              data-testid="steps-streak-badge"
              title={t("streak.tooltip", { target: (streak?.target || 0).toLocaleString(), best: streak?.best || 0 })}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums border transition ${
                streak?.today_hit
                  ? "bg-orange-100 text-orange-800 border-orange-200"
                  : "bg-slate-100 text-slate-600 border-slate-200"
              }`}
            >
              <Flame className="h-3 w-3" />
              {streakDays} {streakDays === 1 ? t("streak.dayOne") : t("streak.dayMany")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center shrink-0">
            <Droplet className="h-4 w-4" />
          </div>
          <p className="text-sm text-slate-700 flex-1 min-w-0"><span className="font-semibold text-slate-900">{waterL.toFixed(2)} l</span> <span className="text-slate-400">/ {targetL.toFixed(1)} l</span></p>
          <button
            type="button"
            onClick={toggleWaterSettings}
            data-testid="water-settings-btn"
            className="h-7 w-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition active:scale-90 shrink-0"
            aria-label="Water settings"
          >
            <Settings2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-4 gap-2 relative" data-testid="water-quick-actions">
        {/* Floating "+250 ml" confirmation chip — rises + fades on any add,
            including the Custom path where the ripple wasn't visible. */}
        {waterFlyChip && (
          <div
            key={waterFlyChip.id}
            data-testid="water-fly-chip"
            className="absolute left-1/2 -translate-x-1/2 -top-4 pointer-events-none z-10 rounded-full bg-sky-500 text-white text-xs font-bold px-2.5 py-1 shadow-lg"
            style={{ animation: "waterFly 900ms ease-out forwards" }}
          >
            +{waterFlyChip.ml} ml
          </div>
        )}
        <style>{`
          @keyframes waterFly {
            0%   { opacity: 0; transform: translate(-50%, 10px) scale(0.85); }
            25%  { opacity: 1; transform: translate(-50%,  0px) scale(1.05); }
            75%  { opacity: 1; transform: translate(-50%, -18px) scale(1); }
            100% { opacity: 0; transform: translate(-50%, -34px) scale(0.9); }
          }
        `}</style>
        {[
          ...(containers.length
            ? containers.map((c, i) => ({
                ml: Number(c.ml),
                label: c.label ? c.label : `+${c.ml} ml`,
                sub: c.label ? `${c.ml} ml` : null,
                tid: `water-quick-container-${i}`,
                color: "text-sky-600 dark:text-sky-300 border-sky-200 dark:border-sky-800/60 bg-sky-50/60 dark:bg-sky-950/40 hover:bg-sky-100/60 dark:hover:bg-sky-900/50",
                kind: "drop",
              }))
            : [
                { ml: 250, label: "+250 ml", tid: "water-quick-plus-025l", color: "text-sky-600 dark:text-sky-300 border-sky-200 dark:border-sky-800/60 bg-sky-50/60 dark:bg-sky-950/40 hover:bg-sky-100/60 dark:hover:bg-sky-900/50", kind: "drop" },
                { ml: 500, label: "+500 ml", tid: "water-quick-plus-05l", color: "text-sky-700 dark:text-sky-200 border-sky-300 dark:border-sky-700 bg-sky-50 dark:bg-sky-900/50 hover:bg-sky-100 dark:hover:bg-sky-800/60", kind: "drop" },
              ]),
          { label: t("cliDash.waterCustom") || "Custom", tid: "water-quick-custom", color: "text-sky-700 dark:text-sky-200 border-sky-200 dark:border-sky-700 bg-white dark:bg-[#0f1213] hover:bg-sky-50/60 dark:hover:bg-sky-950/40", kind: "custom" },
          { label: t("cliDash.waterUndo"), tid: "water-quick-minus-025l", color: "text-slate-500 dark:text-slate-300 border-slate-200 dark:border-slate-700 bg-white dark:bg-[#0f1213] hover:bg-slate-50 dark:hover:bg-slate-800/60", kind: "undo" },
        ].map((btn) => (
          <button
            key={btn.tid}
            onClick={(e) => {
              if (btn.kind === "custom") {
                setCustomOpen((v) => !v);
                return;
              }
              const el = e.currentTarget.querySelector("[data-ripple]");
              if (el) {
                el.classList.remove("animate-[dropRipple_650ms_ease-out]");
                // eslint-disable-next-line no-unused-expressions
                el.offsetWidth;
                el.classList.add("animate-[dropRipple_650ms_ease-out]");
              }
              if (btn.kind === "undo") undoWater();
              else pushWater(btn.ml);
            }}
            data-testid={btn.tid}
            className={`relative overflow-hidden rounded-xl border ${btn.color} h-14 flex flex-col items-center justify-center gap-0.5 active:scale-[0.95] transition-transform`}
          >
            {btn.kind === "drop" && (
              <span
                data-ripple
                className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-14 w-14 rounded-full bg-sky-300/60 opacity-0"
                aria-hidden
              />
            )}
            <span className="relative z-10 flex flex-col items-center gap-0.5 max-w-full px-1">
              {btn.kind === "drop" ? <Droplet className="h-3.5 w-3.5" /> :
               btn.kind === "custom" ? <Plus className="h-3.5 w-3.5" /> :
               <Undo2 className="h-3.5 w-3.5" />}
              <span className="text-[11px] font-semibold max-w-full truncate leading-tight">{btn.label}</span>
              {btn.sub && <span className="text-[9px] text-sky-500 leading-none">{btn.sub}</span>}
            </span>
          </button>
        ))}
      </div>
      {/* Estimated water from logged foods — info only, NOT counted into the drink goal */}
      {foodWaterMl > 0 && (
        <p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500 text-center" data-testid="food-water-info">
          {t("cliDash.foodWater", { ml: foodWaterMl })}
        </p>
      )}
      {waterSettingsOpen && (
        <div className="mt-3 rounded-2xl border border-sky-100 bg-sky-50/50 p-3 space-y-3" data-testid="water-settings-panel">
          <p className="text-xs font-bold text-slate-800">{t("cliDash.waterSettings") || "Wasser-Einstellungen"}</p>
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-slate-600 flex-1">{t("cliDash.waterTargetLabel") || "Tagesziel (Liter)"}</label>
            <input
              type="number" inputMode="decimal" step="0.1" min={0.5} max={8}
              value={draftTarget}
              onChange={(e) => setDraftTarget(e.target.value)}
              data-testid="water-target-input"
              className="w-20 h-9 rounded-lg border border-sky-200 bg-white px-2 text-sm tabular-nums text-right focus:outline-none focus:border-sky-500"
            />
            <span className="text-[11px] text-slate-500 font-semibold">L</span>
          </div>
          <div className="space-y-2">
            <p className="text-[11px] text-slate-600">{t("cliDash.waterContainers") || "Deine Trinkgefässe"}</p>
            {draftContainers.map((c, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`water-container-row-${i}`}>
                <input
                  value={c.label}
                  placeholder={t("cliDash.waterContainerNamePh") || "Name (z. B. Flasche)"}
                  onChange={(e) => setDraftContainers((list) => list.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                  data-testid={`water-container-name-${i}`}
                  className="flex-1 min-w-0 h-9 rounded-lg border border-sky-200 bg-white px-2 text-sm focus:outline-none focus:border-sky-500"
                />
                <input
                  type="number" inputMode="numeric" min={50} max={2000} placeholder="ml"
                  value={c.ml}
                  onChange={(e) => setDraftContainers((list) => list.map((x, j) => (j === i ? { ...x, ml: e.target.value } : x)))}
                  data-testid={`water-container-ml-${i}`}
                  className="w-20 h-9 rounded-lg border border-sky-200 bg-white px-2 text-sm tabular-nums text-right focus:outline-none focus:border-sky-500"
                />
                <button
                  type="button"
                  onClick={() => setDraftContainers((list) => list.filter((_, j) => j !== i))}
                  data-testid={`water-container-remove-${i}`}
                  className="h-9 w-9 rounded-lg border border-slate-200 text-slate-400 hover:text-red-500 flex items-center justify-center shrink-0"
                  aria-label="Remove container"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            {draftContainers.length < 3 && (
              <button
                type="button"
                onClick={() => setDraftContainers((list) => [...list, { label: "", ml: "" }])}
                data-testid="water-container-add"
                className="text-[11px] font-semibold text-sky-600 hover:text-sky-800"
              >
                {t("cliDash.waterAddContainer") || "+ Gefäss"}
              </button>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setWaterSettingsOpen(false)}
              data-testid="water-settings-cancel"
              className="h-9 px-3 rounded-lg border border-slate-200 text-slate-500 text-sm"
            >
              {t("common.cancel") || "Abbrechen"}
            </button>
            <button
              type="button"
              onClick={saveWater}
              disabled={savingWater}
              data-testid="water-settings-save"
              className="h-9 px-4 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold disabled:opacity-50 active:scale-[0.97] transition-transform"
            >
              {savingWater ? "…" : (t("common.save") || "Speichern")}
            </button>
          </div>
        </div>
      )}
      {customOpen && (
        <div className="mt-2 flex items-center gap-2" data-testid="water-custom-editor">
          <div className="relative flex-1">
            <Droplet className="h-4 w-4 text-sky-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={3000}
              placeholder={t("cliDash.waterCustomPh") || "z. B. 330"}
              value={customMl}
              autoFocus
              onChange={(e) => setCustomMl(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitCustom(); }}
              data-testid="water-custom-input"
              className="w-full h-10 rounded-lg border border-sky-200 dark:border-sky-800 bg-white dark:bg-[#0f1213] pl-8 pr-12 text-sm tabular-nums focus:outline-none focus:border-sky-500"
            />
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 font-semibold">ml</span>
          </div>
          <button
            type="button"
            onClick={submitCustom}
            data-testid="water-custom-confirm"
            className="h-10 px-3 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold active:scale-[0.95] transition-transform"
          >
            {t("common.add") || "Aggiungi"}
          </button>
          <button
            type="button"
            onClick={() => { setCustomOpen(false); setCustomMl(""); }}
            data-testid="water-custom-cancel"
            className="h-10 w-10 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-700 flex items-center justify-center"
            aria-label={t("common.cancel") || "Cancel"}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- Expandable FAB (dashboard meal-add menu) ---------- */
function ExpandableFab({ t }) {
  const [open, setOpen] = useState(false);
  const items = [
    { to: "/app/pasto?mode=search", label: t("diary.quickFood"), icon: Utensils, tid: "fab-food" },
    { to: "/app/pasto?mode=photo", label: t("diary.quickPhoto"), icon: Camera, tid: "fab-photo" },
    { to: "/app/allenamenti", label: t("nav.workouts"), icon: Dumbbell, tid: "fab-workout" },
    { to: "/app/dispensa", label: t("nav.pantry"), icon: Refrigerator, tid: "fab-pantry" },
    { to: "/app/menu-settimanale", label: t("weekly.title"), icon: CalendarDays, tid: "fab-weekly" },
    { to: "/app/ricette?fridge=1", label: t("fridge.ctaBtn"), icon: ChefHat, tid: "fab-fridge" },
    { to: "/app/ricette", label: t("nav.recipes"), icon: BookOpen, tid: "fab-recipes" },
  ];
  return (
    <>
      {/* Backdrop */}
      {open && (
        <button
          aria-label="close menu"
          onClick={() => setOpen(false)}
          data-testid="fab-backdrop"
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-30 animate-in fade-in duration-200"
        />
      )}
      <div className="relative z-40" data-testid="fab-container">
        {/* Expanded menu items */}
        {open && (
          <div className="absolute bottom-full left-0 mb-3 space-y-2 min-w-max">
            {items.map((it, i) => (
              <Link
                key={it.tid}
                to={it.to}
                onClick={() => setOpen(false)}
                data-testid={it.tid}
                style={{
                  animationDelay: `${(items.length - i) * 40}ms`,
                  animation: "fab-slide-in 220ms cubic-bezier(0.16,1,0.3,1) both",
                }}
                className="flex items-center gap-3 group"
              >
                <div className="h-14 w-14 rounded-2xl bg-[#5A8B8F] hover:bg-[#4a7679] text-white flex items-center justify-center shadow-lg transition active:scale-95">
                  <it.icon className="h-5 w-5" />
                </div>
                <span className="rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-800 shadow-md">
                  {it.label}
                </span>
              </Link>
            ))}
          </div>
        )}
        {/* Main FAB */}
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="fab-main"
          aria-expanded={open}
          className="h-16 w-16 rounded-2xl bg-[#5A8B8F] hover:bg-[#4a7679] text-white flex items-center justify-center shadow-lg transition active:scale-95"
        >
          {open ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
        </button>
      </div>
    </>
  );
}

/* ---------- Main Dashboard ---------- */
export default function ClientDashboard() {
  const { user, refresh: refreshUser } = useAuth();
  const { t, lang } = useT();
  const { isEnabled } = useEnabledFeatures();
  const navigate = useNavigate();

  // Native app: silently pull fresh HealthKit / Health Connect data
  // (throttled to every 6h inside the helper). No-op on web/PWA.
  useEffect(() => { autoSyncNativeHealthIfDue(); }, []);

  // Deep-link hash support. When the user taps a push notification like
  // "💧 Hydration reminder" the SW rewrites to `/app#water`, and here we
  // scroll the WATER stat cell into view + briefly pulse it so it's
  // obvious where to tap next. Same pattern applies to `#steps`,
  // `#kcal`, `#sleep`, `#workout`, `#goals` — any StatCell has a stable
  // `data-testid="daily-<key>"` we can target.
  useEffect(() => {
    const focusCell = () => {
      const hash = (window.location.hash || "").replace(/^#/, "");
      if (!hash) return;
      const map = { water: "daily-water", steps: "daily-steps", kcal: "daily-kcal",
                    sleep: "daily-sleep", workout: "daily-workout", goals: "daily-goals" };
      const testId = map[hash];
      if (!testId) return;
      // DailySummaryCard renders async once the profile / vitals load,
      // so the target testid may not yet be in the DOM on first paint.
      // Try up to 3× at 200ms intervals before giving up.
      let attempts = 0;
      const tryFocus = () => {
        const el = document.querySelector(`[data-testid="${testId}"]`);
        if (!el) {
          if (++attempts < 6) setTimeout(tryFocus, 250);
          return;
        }
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Brief pulse using existing Tailwind utility so we don't add a
        // bespoke keyframe just for this.
        el.classList.add("ring-2", "ring-emerald-400", "animate-pulse");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-emerald-400", "animate-pulse");
        }, 2600);
      };
      requestAnimationFrame(tryFocus);
    };
    focusCell();
    window.addEventListener("hashchange", focusCell);
    return () => window.removeEventListener("hashchange", focusCell);
  }, []);

  const [date, setDate] = useState(todayIso());
  // Hydrate from the last-seen snapshot so the screen shows real values at once;
  // the network refresh below overwrites them within a second.
  const dashCache = useMemo(() => readDashCache(user?.user_id), [user?.user_id]);
  const cachedToday = dashCache && dashCache.date === todayIso() ? dashCache : null;
  const [profile, setProfile] = useState(dashCache?.profile || {});
  const [plan, setPlan] = useState(dashCache?.plan || null);
  const [foodLogs, setFoodLogs] = useState(cachedToday?.foodLogs || []);
  const [waterGlasses, setWaterGlasses] = useState(cachedToday?.waterGlasses || 0);
  // Micros drawer preference — collapsed by default (users found the full
  // Zucker/Ballast/Gesätt/Salz row noisy). Choice persisted per-user in
  // localStorage so their toggle sticks across sessions.
  const [microsOpen, setMicrosOpen] = useState(() => {
    try { return localStorage.getItem("solano_micros_open") === "1"; } catch { return false; }
  });
  const [expandedMeals, setExpandedMeals] = useState(new Set());
  // Ref to the standalone VoiceFoodButton so the QuickTrackMenu can
  // programmatically click it, preserving the direct user-gesture chain
  // that iOS Safari requires for mic access.
  const voiceBtnRef = useRef(null);
  // Meal type captured at the moment the user taps "Stimme" in a specific
  // meal row's QuickTrackMenu — read when the transcription comes back so
  // the food is logged into the right meal. `null` means "auto-bucket by
  // time of day" (Dashboard-header Stimme button).
  const voicePendingMealTypeRef = useRef(null);
  const toggleMealGroup = (mt) => setExpandedMeals((prev) => {
    const next = new Set(prev);
    if (next.has(mt)) next.delete(mt); else next.add(mt);
    return next;
  });
  const [vitals, setVitals] = useState(cachedToday?.vitals || {});
  const [stepsStreak, setStepsStreak] = useState(cachedToday?.stepsStreak || { current: 0, best: 0, target: 10000, today_hit: false });
  const [workoutSummary, setWorkoutSummary] = useState(cachedToday?.workoutSummary || { count: 0, total_min: 0, calories_burned: 0 });
  // Which date the daily data was actually LOADED for. Guards the celebration
  // overlays against stale state: on the morning day-rollover `date` flips to
  // today while workoutSummary/vitals still hold yesterday's values for a
  // moment — without this guard the training/steps/sleep mascots fired at 0.
  const loadedForDateRef = useRef(null);
  const [searchDialog, setSearchDialog] = useState({ open: false, mealType: null, initialQuery: "", initialQuantityG: null });
  const [multiSheet, setMultiSheet] = useState({ open: false, items: [], pendingIdx: null });
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  // First-login tutorial trigger. Fetches onboarding status once — if the
  // user has never completed it, we auto-show the wizard. Waits until the
  // consent modal (BetaConsentModal) is done, otherwise the two dialogs
  // stack and the consent layer swallows the wizard's taps.
  const needsConsent = !!(user && user.consent && user.consent.needs_consent);
  useEffect(() => {
    if (needsConsent) return;
    let cancelled = false;
    api.get("/client/onboarding/status")
      .then(({ data }) => {
        if (!cancelled && data && !data.done) setOnboardingOpen(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [needsConsent]);
  const [weights, setWeights] = useState(dashCache?.weights || []);
  const [newWeight, setNewWeight] = useState("");
  const [kcalDialogOpen, setKcalDialogOpen] = useState(false);
  const [newKcalTarget, setNewKcalTarget] = useState("");
  const [calcOpen, setCalcOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);

  const isToday = date === todayIso();
  const localeMap = { it: "it-IT", en: "en-GB", de: "de-DE" };
  const shortDate = new Date(date).toLocaleDateString(localeMap[lang] || "it-IT", { day: "numeric", month: "short" });
  const dateLabel = isToday
    ? t("cliDash.today")
    : date === shiftIso(todayIso(), -1)
      ? t("cliDash.yesterday")
      : date === shiftIso(todayIso(), 1)
        ? t("cliDash.tomorrow")
        : shortDate;

  // Auto-roll the date forward when midnight passes with the app still open.
  // Only fires when the calendar day ACTUALLY changed since the last check —
  // otherwise a focus event while deliberately browsing "yesterday" would
  // snap the user straight back to today (Sept 2026 bug).
  const todayRollRef = useRef(todayIso());
  useEffect(() => {
    const check = () => {
      const now = todayIso();
      if (now !== todayRollRef.current) {
        const prevToday = todayRollRef.current;
        todayRollRef.current = now;
        setDate((prev) => (prev === prevToday ? now : prev));
      }
    };
    const onVis = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", check);
    const iv = setInterval(check, 5 * 60 * 1000);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", check);
      clearInterval(iv);
    };
  }, []);

  const loadStatic = async () => {
    const [{ data: p }, { data: mp }, { data: w }] = await Promise.all([
      api.get("/client/profile"),
      api.get("/client/meal-plan"),
      api.get("/client/weight"),
    ]);
    setProfile(p);
    setPlan(mp && mp.plan_id ? mp : null);
    setWeights(w);
    writeDashCache(user?.user_id, { profile: p, plan: mp && mp.plan_id ? mp : null, weights: w });
  };

  const loadDaily = async (d) => {
    const [{ data: logs }, { data: water }, timelineResp, workoutResp, streakResp] = await Promise.all([
      api.get(`/client/food-log?date=${d}`),
      api.get(`/client/water?date=${d}`),
      // Unified wearable timeline (WHOOP + Manual + Apple + Vital-legacy)
      // — replaces the legacy /client/vital/metrics call so WHOOP-synced
      // values (Sleep/HRV/RHR/Steps) surface on the Dashboard automatically.
      api.get(`/client/wearable/timeline?days=60`).catch(() => ({ data: { days: [] } })),
      api.get(`/workouts/summary?date=${d}`).catch(() => ({ data: null })),
      api.get(`/client/steps/streak`).catch(() => ({ data: null })),
    ]);
    setFoodLogs(logs || []);
    // Prefer `ml` (precise) over `glasses` (integer) so a Custom 330 ml
    // entry survives round-trips instead of collapsing to the nearest 250.
    const wml = water?.ml;
    const loadedGlasses = wml != null ? wml / GLASS_ML : (water?.glasses || 0);
    setWaterGlasses(loadedGlasses);

    // Water queued via the widget's +250/+500 buttons while the app was
    // closed → merge into today's count and persist to the backend.
    if (d === todayIso()) {
      try {
        const pendMl = await drainPendingWidgetWater();
        if (pendMl > 0) {
          const next = Math.max(0, Math.min(20, loadedGlasses + pendMl / GLASS_ML));
          setWaterGlasses(next);
          api.post("/client/water", {
            date: d,
            glasses: Math.round(next),
            ml: Math.round(next * GLASS_ML),
          }).catch(() => {});
        }
      } catch {}
    }

    // WHOOP typically publishes each day's sleep/recovery only after the
    // wake-up sync — so on the current day the merged row for `d` may
    // still be empty. Backfill each field from the most recent day that
    // has it (max look-back = the 60 days we already fetched). Each
    // backfilled field remembers its source-date so the tile can show a
    // small "gestern" / "vor 2t" hint instead of pretending it's live.
    const allDays = ((timelineResp.data || {}).days || []).slice().sort((a, b) => a.date.localeCompare(b.date));
    const today = allDays.find((v) => v.date === d) || {};
    const backfill = (key) => {
      if (today[key] != null) return { value: today[key], from: null, source: today.sources?.[key] };
      for (let i = allDays.length - 1; i >= 0; i--) {
        const row = allDays[i];
        if (row.date >= d) continue;   // strictly older than the selected day
        if (row[key] != null) return { value: row[key], from: row.date, source: row.sources?.[key] };
      }
      return { value: null, from: null, source: null };
    };
    const sleepBF = backfill("sleep_duration_minutes");
    // Steps: NO yesterday-fallback (same principle as the TRAINING tile) —
    // a new day starts at 0 and counts up as soon as today's steps sync in.
    const todaySteps = today.steps != null ? today.steps : 0;
    // WHOOP workouts are stored as an array on the daily row. Sum durations
    // (end - start) and active calories so the TRAINING tile + "Your
    // goals" line show the WHOOP-captured session even without a manual
    // log — Sofia's clients rarely double-enter what the strap already
    // recorded.
    const workoutsFor = (row) => {
      const items = (row?.workouts || []);
      let minutes = 0, calories = 0;
      const perItem = [];
      for (const w of items) {
        let m = 0;
        if (w?.start && w?.end) {
          try {
            const s = new Date(w.start).getTime();
            const e = new Date(w.end).getTime();
            if (Number.isFinite(s) && Number.isFinite(e) && e > s) {
              m = Math.round((e - s) / 60000);
            }
          } catch {}
        }
        let k = 0;
        if (typeof w?.active_calories === "number") k = w.active_calories;
        else if (typeof w?.kilojoules === "number")  k = w.kilojoules / 4.184; // kJ → kcal
        minutes += m; calories += k;
        perItem.push({ min: m, kcal: Math.round(k) });
      }
      return { minutes, count: items.length, calories: Math.round(calories), items: perItem };
    };
    // No yesterday-fallback: if nothing was trained TODAY the tile shows 0.
    // (Showing "49 min · Gestern" confused users into thinking it was today.)
    const todayWearable = workoutsFor(today);

    // A workout logged manually TODAY must win over the yesterday-fallback
    // from the wearable — otherwise the tile keeps showing "49 min ·
    // yesterday" even though the user just logged 30 min for today.
    const manual = workoutResp.data || { count: 0, total_min: 0, calories_burned: 0 };
    const hasToday = (manual.total_min || 0) > 0 || todayWearable.minutes > 0;

    setVitals({
      ...today,
      sleep_duration_minutes:  sleepBF.value,
      sleep_from:              sleepBF.from,
      steps:                   todaySteps,
      total_steps:             todaySteps,
      steps_from:              null,
      sources: {
        ...(today.sources || {}),
        ...(sleepBF.source ? { sleep_duration_minutes: sleepBF.source } : {}),
      },
      wearable_workout_min:   todayWearable.minutes,
      wearable_workout_from:  null,
    });

    if (hasToday) {
      // Same merge rule as the Training page: manual logs + wearable sessions
      // are SUMMED, dropping wearable items that duplicate a manual log
      // (duration within ±5 min). Previously Math.max() mixed both sources
      // into a chimera (WHOOP minutes + manual kcal) that never matched the
      // Training page totals.
      const manualDur = manual.durations || [];
      const extra = (todayWearable.items || []).filter(
        (w) => !manualDur.some((md) => Math.abs((md || 0) - (w.min || 0)) <= 2)
      );
      const extraMin  = extra.reduce((s, w) => s + (w.min || 0), 0);
      const extraKcal = extra.reduce((s, w) => s + (w.kcal || 0), 0);
      setWorkoutSummary({
        ...manual,
        total_min:       (manual.total_min || 0)       + extraMin,
        count:           (manual.count || 0)           + extra.length,
        calories_burned: (manual.calories_burned || 0) + extraKcal,
      });
    } else {
      setWorkoutSummary({ ...manual, total_min: 0, count: 0, calories_burned: 0 });
    }
    if (streakResp?.data) setStepsStreak(streakResp.data);
    loadedForDateRef.current = d;
  };

  // Persist today's snapshot for an instant first paint on the next app open.
  useEffect(() => {
    const today = todayIso();
    if (loadedForDateRef.current !== today || date !== today) return;
    writeDashCache(user?.user_id, { date: today, foodLogs, waterGlasses, vitals, stepsStreak, workoutSummary });
  }, [foodLogs, waterGlasses, vitals, stepsStreak, workoutSummary, date, user?.user_id]);

  // Silently pull fresh wearable data if the last sync is stale (>30 min).
  // Covers both the legacy Vital.io pipeline and the direct WHOOP
  // integration — whichever is connected. Runs once per Dashboard mount,
  // best-effort (never blocks render, never surfaces errors).
  useEffect(() => {
    (async () => {
      try {
        const { data: s } = await api.get("/client/vital/status");
        if (s?.has_vital_user) {
          const stale = !s.last_sync || (Date.now() - new Date(s.last_sync).getTime()) > 30 * 60 * 1000;
          if (stale) {
            const { data: r } = await api.post("/client/vital/sync?days=7");
            if (r?.days_updated > 0) loadDaily(date);
          }
        }
      } catch { /* silent */ }
      try {
        // WHOOP: /client/wearable-connections returns per-provider last_sync_at.
        const { data: wc } = await api.get("/client/wearable-connections");
        const whoop = (wc?.connections || []).find((c) => c.provider === "whoop");
        if (!whoop?.connected || !whoop?.enabled) return;
        const stale = !whoop.last_sync_at || (Date.now() - new Date(whoop.last_sync_at).getTime()) > 30 * 60 * 1000;
        if (!stale) return;
        const { data: r } = await api.post("/client/whoop/sync?days=7");
        if (r?.synced?.days > 0) loadDaily(date);
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadStatic().catch(() => {}); }, []);
  useEffect(() => { loadDaily(date).catch(() => {}); }, [date]);

  // Pull-to-refresh → force a fresh Apple-Health sync (last 2 days), then
  // re-fetch everything the Dashboard shows.
  useEffect(() => registerPullRefresh(async () => {
    if (isNativeHealthSupported()) {
      try { await syncNativeHealth(2); } catch { /* health optional */ }
    }
    await Promise.allSettled([loadStatic(), loadDaily(date)]);
    refreshWidgetFromServer();
  }), [date]);

  // Widget deep link solanoessence://water → scroll to & highlight the
  // water card. `ts` param makes repeat taps re-trigger the effect.
  const location = useLocation();
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (!p.get("water")) return;
    const timer = setTimeout(() => {
      const el = document.getElementById("water-quick-card");
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-[#38BDF8]", "ring-offset-2");
      setTimeout(() => el.classList.remove("ring-2", "ring-[#38BDF8]", "ring-offset-2"), 2600);
    }, 500);
    return () => clearTimeout(timer);
  }, [location.search]);

  /* Aggregate today's macros AND micros */
  const totals = useMemo(() => {
    return foodLogs.reduce((acc, l) => ({
      calories: acc.calories + (l.calories || 0),
      protein: acc.protein + (l.protein || 0),
      carbs: acc.carbs + (l.carbs || 0),
      fat: acc.fat + (l.fat || 0),
      sugar: acc.sugar + (l.sugar || 0),
      fiber: acc.fiber + (l.fiber || 0),
      saturated_fat: acc.saturated_fat + (l.saturated_fat || 0),
      salt: acc.salt + (l.salt || 0),
    }), { calories: 0, protein: 0, carbs: 0, fat: 0, sugar: 0, fiber: 0, saturated_fat: 0, salt: 0 });
  }, [foodLogs]);

  // Estimated water from today's logged foods (info only)
  const foodWaterMl = useMemo(
    () => foodLogs.reduce((a, l) => a + (l.water_ml || 0), 0),
    [foodLogs]
  );
  const baseKcalTarget = profile?.daily_calories_target || plan?.total_calories || 0;
  // Per-DAY toggle: reads/writes the override of the VIEWED date (not the
  // real today) so browsing yesterday/tomorrow shows THAT day's own choice.
  // Days without an override fall back to the legacy global flag.
  const wkDays = profile?.workout_kcal_in_budget_days || {};
  const kcalBudgetToday = wkDays[date] !== undefined ? !!wkDays[date] : !!profile?.workout_kcal_in_budget;
  const workoutBonus = kcalBudgetToday ? Math.round(workoutSummary?.calories_burned || 0) : 0;
  const kcalTarget = baseKcalTarget + workoutBonus;
  const proteinTarget = profile?.protein_target_g || plan?.total_protein || 0;
  const carbsTarget = profile?.carbs_target_g || plan?.total_carbs || 0;
  const fatTarget = profile?.fat_target_g || plan?.total_fat || 0;

  const waterL = (waterGlasses * GLASS_ML) / ML_PER_LITER;
  const waterTargetL = Number(profile?.water_target_l) > 0 ? Number(profile.water_target_l) : 2.0;

  const [waterMilestoneShow, setWaterMilestoneShow] = useState(false);
  const [trainingShow, setTrainingShow] = useState(false);
  const showWaterMilestoneOnce = () => {
    try {
      const key = `water_2l_shown_${date}`;
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, "1");
        setWaterMilestoneShow(true);
      }
    } catch { /* ignore */ }
  };
  // Water added via the DailySummaryCard quick-add tile crosses the goal →
  // the tile dispatches this event (it has no access to the overlay).
  useEffect(() => {
    const onGoal = () => { if (date === todayIso()) showWaterMilestoneOnce(); };
    window.addEventListener("solano:water-goal", onGoal);
    return () => window.removeEventListener("solano:water-goal", onGoal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);
  // Steps mascot: fires once per day as soon as today's synced steps cross
  // the personal step target (Apple Health / wearable / manual).
  const [stepsGoalShow, setStepsGoalShow] = useState(false);
  const stepsToday = vitals?.total_steps || vitals?.steps || 0;
  const stepsGoalTarget = stepsStreak?.target || 10000;
  useEffect(() => {
    if (date !== todayIso()) return;
    if (loadedForDateRef.current !== date) return; // stale data from previous day
    if (!stepsToday || stepsToday < stepsGoalTarget) return;
    try {
      const key = `steps_goal_shown_${date}`;
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, "1");
        setStepsGoalShow(true);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepsToday, stepsGoalTarget, date]);
  // Sleep mascot: fires once per day (morning) when TODAY's actual synced
  // sleep (not a backfilled value from a previous day) reaches the 8h target.
  const [sleepGoalShow, setSleepGoalShow] = useState(false);
  // Last night's sleep counts for the morning mascot even when WHOOP filed it
  // under yesterday's date (sleep_from === yesterday, shown as "Gestern" hint).
  // Older backfills (2+ days) stay excluded — that's genuinely stale data.
  const sleepMinToday =
    vitals?.sleep_from == null || vitals?.sleep_from === shiftIso(todayIso(), -1)
      ? (vitals?.sleep_duration_minutes || 0)
      : 0;
  useEffect(() => {
    if (date !== todayIso()) return;
    if (loadedForDateRef.current !== date) return; // stale data from previous day
    if (!sleepMinToday || sleepMinToday < 8 * 60) return;
    try {
      const key = `sleep_goal_shown_${date}`;
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, "1");
        setSleepGoalShow(true);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleepMinToday, date]);
  // Training mascot: fires once per day as soon as ANY workout shows up for
  // today (manual log, photo log or Apple Health / wearable sync). The
  // Workouts page sets the same key when it celebrates, so no double popup.
  useEffect(() => {
    if (date !== todayIso()) return;
    if (loadedForDateRef.current !== date) return; // stale data from previous day
    const hasWorkout = (workoutSummary?.count || 0) > 0 || (workoutSummary?.total_min || 0) > 0;
    if (!hasWorkout) return;
    try {
      const key = `training_shown_${date}`;
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, "1");
        setTrainingShow(true);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workoutSummary, date]);
  const addWater = async (deltaGlasses) => {
    const prev = waterGlasses;
    const next = Math.max(0, Math.min(40, waterGlasses + deltaGlasses));
    setWaterGlasses(next);
    // Detect first-time crossing of the personal daily target. Show mascot
    // celebration ONCE per day (localStorage key). Only fires on increments
    // so decreasing then re-crossing doesn't spam the user.
    try {
      const prevL = (prev * GLASS_ML) / ML_PER_LITER;
      const nextL = (next * GLASS_ML) / ML_PER_LITER;
      if (deltaGlasses > 0 && prevL < waterTargetL && nextL >= waterTargetL) {
        showWaterMilestoneOnce();
      }
    } catch {}
    try {
      // Backend expects `glasses: int`. When the user picks a Custom ml
      // value (e.g. 330 ml → 1.32 glasses) we send the ml as the precise
      // source of truth and round glasses so Pydantic accepts the payload.
      await api.post("/client/water", {
        date,
        glasses: Math.round(next),
        ml: Math.round(next * GLASS_ML),
      });
    } catch { toast.error(t("common.error")); loadDaily(date); }
  };

  // Mirror today's numbers into the iOS widgets — SINGLE SOURCE OF TRUTH:
  // always the server endpoint (/client/widget-stats). Pushing locally
  // computed totals caused two writers overwriting each other with
  // inconsistent values (e.g. 185/2'019 vs 957/2'000).
  useEffect(() => {
    if (date !== todayIso()) return;
    refreshWidgetFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totals.calories, totals.protein, kcalTarget, proteinTarget, waterL, vitals, workoutSummary, date]);

  const logWeight = async () => {
    if (!newWeight) return;
    try {
      await api.post("/client/weight", { weight_kg: +newWeight, date: todayIso() });
      toast.success(t("cliDash.weightLogged"));
      setNewWeight("");
      const { data: w } = await api.get("/client/weight");
      setWeights(w);
    } catch { toast.error(t("common.error")); }
  };

  const saveKcalTarget = async () => {
    const val = parseInt(newKcalTarget, 10);
    if (!val || val < 500 || val > 6000) {
      toast.error(t("cliDash.kcalRange"));
      return;
    }
    try {
      await api.put("/client/profile", { daily_calories_target: val });
      toast.success(t("cliDash.targetSaved"));
      setKcalDialogOpen(false);
      loadStatic();
    } catch { toast.error(t("common.error")); }
  };

  const start = weights[0]?.weight_kg;
  const latest = weights[weights.length - 1]?.weight_kg;
  const delta = start && latest ? (latest - start).toFixed(1) : null;

  const firstName = user?.name?.split(" ")[0] || "";

  return (
    <Layout role="client">
      <ProWelcomeTour />
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-6 md:py-8 pb-28">

        {/* Greeting */}
        <div className="flex items-center gap-3 mb-6" data-testid="dashboard-greeting">
          <div className="relative shrink-0">
            <AvatarUploader user={user} refreshUser={refreshUser} firstName={firstName} t={t} />
            <ProStatusBadge />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-[#1A1D1E] dark:text-white flex-1 min-w-0 truncate">
            {t("cliDash.hi")} {firstName}
          </h1>
          <Mascot size={64} pose="static" alt="Solano" className="shrink-0" />
          <QuickTrackMenu date={date} onSaved={() => loadDaily(date)} onVoice={() => { voicePendingMealTypeRef.current = null; voiceBtnRef.current?.beginRecording?.(); }} />
        </div>

        {/* Main dashboard stack — 16px vertical rhythm between every top-level block */}
        <div className="space-y-4">

        <DashboardScoresRow date={date} />

        <DailySummaryCard
          t={t}
          sleepMinutes={vitals?.sleep_duration_minutes || 0}
          sleepFromDate={vitals?.sleep_from}
          steps={vitals?.total_steps || vitals?.steps || 0}
          stepsTarget={stepsStreak?.target || 10000}
          stepsFromDate={vitals?.steps_from}
          waterL={waterL}
          waterTargetL={waterTargetL}
          kcalConsumed={totals.calories}
          kcalTarget={kcalTarget}
          workoutMin={workoutSummary?.total_min || 0}
          workoutFromDate={vitals?.wearable_workout_from}
          sources={vitals?.sources || {}}
        />

        <AiQuotaChip />

        {/* Auto goal review — suggests ±100 kcal after 2–4 weeks of real progress */}
        <GoalReviewCard onApplied={() => loadStatic()} />

        {isEnabled("gamification") && <GamificationWidget />}

        {isEnabled("plate_solano") && <PlateTodayCard date={date} />}


        {/* Section header: Goals + date navigator */}
        <div className="flex items-center justify-between pt-2">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1D1E]">
            {t("cliDash.goals")}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDate(shiftIso(date, -1))}
              data-testid="date-prev"
              className="h-9 w-9 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 active:scale-95 transition"
              aria-label="previous day"
            >
              <ChevronLeft className="h-4 w-4 text-slate-600" />
            </button>
            <button
              onClick={() => setDate(todayIso())}
              data-testid="date-today"
              className="min-w-[68px] px-3 h-9 rounded-full text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
            >
              {dateLabel}
            </button>
            <button
              onClick={() => setDate(shiftIso(date, 1))}
              data-testid="date-next"
              className="h-9 w-9 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 active:scale-95 transition"
              aria-label="next day"
            >
              <ChevronRight className="h-4 w-4 text-slate-600" />
            </button>
          </div>
        </div>

        {/* Goals row: Calorie ring + Fitness card + Macros bar
            On mobile the order is: Ring → Macros → Fitness (user preference).
            On desktop the ring and fitness sit side-by-side and macros spans below. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Calorie Ring Card */}
          <button
            type="button"
            onClick={() => setCalcOpen(true)}
            data-testid="calorie-card"
            className="order-1 rounded-3xl bg-white p-5 shadow-sm border border-slate-100 min-h-[220px] text-left hover:border-[#5A8B8F]/40 hover:shadow-md active:scale-[0.99] transition group relative"
            aria-label="edit calorie target"
          >
            <div className="flex items-center justify-between">
              <p className="font-heading text-lg font-semibold text-[#1A1D1E]">{t("cliDash.calories")}</p>
              <span className="opacity-0 group-hover:opacity-100 transition text-slate-400">
                <Pencil className="h-3.5 w-3.5" />
              </span>
            </div>
            <div className="mt-2">
              <CalorieRing consumed={totals.calories} target={kcalTarget} />
              <p className="text-center text-xs text-slate-500 mt-2">
                {kcalTarget
                  ? (totals.calories > kcalTarget ? t("cliDash.kcalOver") : `${t("cliDash.kcalLeft")} · ${t("cliDash.of")} ${kcalTarget}`)
                  : <span className="text-[#E8951C]" data-testid="set-kcal-target">{t("cliDash.setKcalTarget")} →</span>
                }
              </p>
            </div>

            {kcalTarget > 0 && (
              <>
                {/* Consumate / Bruciate — subtle stat row below the ring, gerarchia moderna */}
                <div className="mt-4 flex items-center justify-center gap-6 text-center" data-testid="cal-ministat">
                  <div className="flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#5A8B8F]" aria-hidden />
                    <div className="text-left">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold leading-none">
                        {t("cliDash.kcalConsumed")}
                      </p>
                      <p className="font-heading text-base font-bold text-[#1A1D1E] tabular-nums leading-tight">
                        {Math.round(totals.calories)}
                      </p>
                    </div>
                  </div>
                  <div className="h-8 w-px bg-slate-200" aria-hidden />
                  <div className="flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#E85D1C]" aria-hidden />
                    <div className="text-left">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold leading-none">
                        {t("cliDash.kcalBurnedShort")}
                      </p>
                      <p className="font-heading text-base font-bold text-[#1A1D1E] tabular-nums leading-tight">
                        {Math.round(workoutBonus || 0)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* c. Workout bonus badge */}
                {workoutBonus > 0 && (
                  <div className="mt-2 flex items-center justify-center gap-1.5 text-[11px]" data-testid="workout-bonus">
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#FEE7DD] text-[#8A3A10] font-semibold px-2 py-0.5">
                      🔥 +{Math.round(workoutBonus)} {t("cliDash.workoutBonus")}
                    </span>
                  </div>
                )}

                {/* c.2 Quick toggle: fold workout kcal into daily budget.
                       Modern iOS-style switch — bigger tap target, smooth
                       spring animation, gradient ON state, muted OFF. */}
                <div className="mt-3 flex items-center justify-center" data-testid="workout-budget-toggle-row">
                  <span
                    role="switch"
                    tabIndex={0}
                    aria-checked={kcalBudgetToday}
                    data-testid="workout-budget-toggle"
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); e.currentTarget.click(); } }}
                    onClick={async (e) => {
                      // The whole "Calories" card is a <button> that opens
                      // the kcal-target dialog. Nested-button clicks bubble
                      // → without stopPropagation this pill flips the
                      // toggle AND pops the modal on top. Kill the bubble.
                      e.stopPropagation();
                      e.preventDefault();
                      const next = !kcalBudgetToday;
                      const nextDays = { ...wkDays, [date]: next };
                      setProfile((p) => ({ ...p, workout_kcal_in_budget_days: nextDays }));
                      try {
                        await api.put("/client/profile", { workout_kcal_in_budget_days: nextDays });
                        toast.success(next
                          ? (t("workouts.kcalBudgetOn")  || "Workout-kcal heute im Budget")
                          : (t("workouts.kcalBudgetOff") || "Workout-kcal heute separat"));
                      } catch {
                        setProfile((p) => ({ ...p, workout_kcal_in_budget_days: wkDays }));
                        toast.error(t("common.error") || "Fehler");
                      }
                    }}
                    className={`group inline-flex cursor-pointer select-none items-center gap-2.5 rounded-full pl-1.5 pr-3.5 py-1.5 border transition-all duration-200 active:scale-[0.97] ${
                      kcalBudgetToday
                        ? "bg-gradient-to-r from-orange-500 to-amber-500 border-transparent text-white shadow-sm shadow-orange-200"
                        : "bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                    title={t("workouts.settingKcalInBudgetHelp")}
                  >
                    <span className={`h-5 w-9 rounded-full relative transition-colors duration-300 shrink-0 ${
                      kcalBudgetToday ? "bg-white/25" : "bg-slate-200"
                    }`}>
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full shadow-md transition-all duration-300 ease-out flex items-center justify-center ${
                        kcalBudgetToday
                          ? "translate-x-4 bg-white"
                          : "translate-x-0.5 bg-white"
                      }`}>
                        {kcalBudgetToday && (
                          <Flame className="h-2.5 w-2.5 text-orange-500" />
                        )}
                      </span>
                    </span>
                    <span className="text-[11px] font-semibold whitespace-nowrap">
                      {t("workouts.kcalBudgetShort") || "Kcal-Budget mit Workout"}
                    </span>
                  </span>
                </div>

                {/* d. Next meal suggestion */}
                {(() => {
                  const loggedByMeal = foodLogs.reduce((acc, l) => {
                    const mt = (l.meal_type || "").toLowerCase();
                    if (mt) acc[mt] = (acc[mt] || 0) + (l.calories || 0);
                    return acc;
                  }, {});
                  const nextMeal = pickNextMeal(new Date().getHours(), loggedByMeal, t);
                  if (!nextMeal) {
                    return (
                      <p className="mt-2 text-center text-[11px] text-emerald-600 font-medium" data-testid="next-meal">
                        {t("cliDash.nextMealDone")}
                      </p>
                    );
                  }
                  const suggested = Math.round(kcalTarget * nextMeal.ratio);
                  return (
                    <p className="mt-2 text-center text-[11px] text-slate-500" data-testid="next-meal">
                      🍽️ {t("cliDash.nextMealSuggest", { meal: nextMeal.label, kcal: suggested })}
                    </p>
                  );
                })()}
              </>
            )}
          </button>

          {/* Macros bar - shown after ring on mobile, spans full width below on desktop */}
          <div className="order-2 sm:order-3 sm:col-span-2 rounded-3xl bg-white p-5 shadow-sm border border-slate-100" data-testid="macros-card">
            <div className="flex items-start gap-4">
              <MacroBar label={t("cliDash.carbs")} value={totals.carbs} target={carbsTarget} accent="#22C55E" />
              <MacroBar label={t("cliDash.protein")} value={totals.protein} target={proteinTarget} accent="#EF4444" />
              <MacroBar label={t("cliDash.fat")} value={totals.fat} target={fatTarget} accent="#F59E0B" />
            </div>
            <button
              type="button"
              onClick={() => {
                const next = !microsOpen;
                setMicrosOpen(next);
                try { localStorage.setItem("solano_micros_open", next ? "1" : "0"); } catch {}
              }}
              data-testid="micros-toggle-btn"
              aria-expanded={microsOpen}
              className="mt-4 w-full flex items-center justify-between gap-2 text-xs font-semibold text-slate-500 hover:text-[#2C5254] transition py-2"
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="h-px w-6 bg-slate-200" />
                {microsOpen
                  ? (t("cliDash.hideDetails") || "Nascondi dettagli")
                  : (t("cliDash.showDetails") || "Zeige Zucker · Ballast · Salz")}
              </span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${microsOpen ? "rotate-180" : ""}`} />
            </button>
            {microsOpen && (
            <div className="mt-1 pt-3 border-t border-slate-100 space-y-2.5" data-testid="micros-list">
              {[
                { label: t("cliDash.sugar"), value: totals.sugar, target: 50, tid: "sugar" },
                { label: t("cliDash.fiber"), value: totals.fiber, target: 30, tid: "fiber" },
                { label: t("cliDash.satFat"), value: totals.saturated_fat, target: 22, tid: "sat-fat" },
                { label: t("cliDash.salt"), value: totals.salt, target: 5, tid: "salt" },
              ].map((m) => {
                const pct = m.target > 0 ? Math.min(100, (m.value / m.target) * 100) : 0;
                return (
                  <div key={m.tid} className="flex items-center gap-3" data-testid={`micro-row-${m.tid}`}>
                    <span className="text-xs font-medium text-slate-600 w-16 shrink-0">{m.label}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-slate-400 transition-[width] duration-1000 ease-out" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs text-slate-500 tabular-nums w-20 text-right shrink-0">
                      <span className="font-medium text-slate-700">{m.value.toFixed(1)}g</span>
                      <span className="text-slate-400"> / {m.target}g</span>
                    </span>
                  </div>
                );
              })}
              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={() => toast.info(t("cliDash.microsInfo"))}
                  data-testid="micros-info"
                  className="h-6 w-6 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-50 flex items-center justify-center transition"
                  aria-label="micros info"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            )}
          </div>

          {/* Fitness Card - shown last on mobile (below macros), side-by-side with ring on desktop */}
          <div className="order-3 sm:order-2 space-y-4">
            <FitnessCard
              t={t}
              /* Top line = daily activity WITHOUT training. Apple Health's
                 `active_energy` includes workout burn, so subtract the merged
                 workout kcal. Without Apple Health, estimate from steps
                 (~0.04 kcal/step). */
              kcalBurned={(() => {
                const wk = Math.round(workoutSummary?.calories_burned || 0);
                const st = vitals?.total_steps || vitals?.steps || 0;
                const stepEst = Math.round(st * 0.04);
                if (vitals?.active_calories != null) {
                  const base = Math.round(vitals.active_calories - wk);
                  // WHOOP reports mostly workout kcal as "active", so the
                  // subtraction can land near 0 despite thousands of steps —
                  // never show less than the step-based estimate.
                  return Math.max(base, stepEst);
                }
                return stepEst;
              })()}
              steps={vitals?.total_steps || vitals?.steps || 0}
              waterL={waterL}
              targetL={waterTargetL}
              addWater={addWater}
              workoutMin={workoutSummary?.total_min || 0}
              workoutCount={workoutSummary?.count || 0}
              workoutKcal={Math.round(workoutSummary?.calories_burned || 0)}
              streak={stepsStreak}
              date={date}
              profile={profile}
              saveWaterSettings={async (payload) => {
                const { data } = await api.put("/client/profile", payload);
                setProfile(data);
                try { refreshWidgetFromServer(); } catch { /* noop */ }
              }}
              foodWaterMl={foodWaterMl}
            />
            <DailyCheckinCard t={t} date={date} />
          </div>
        </div>
        </div>{/* end main-stack space-y-4 */}

        {/* Meals section */}
        <div className="mt-8 mb-4 flex items-center justify-between">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1D1E]">
            {t("cliDash.mealsToday")}
          </h2>
          <div className="flex items-center gap-2">
            <Link to="/app/ricette" data-testid="link-recipes"
              className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition">
              <ShoppingBag className="h-4 w-4 text-slate-600" />
            </Link>
          </div>
        </div>

        {/* Meals grouped by type - collapsible accordions */}
        <div className="space-y-2" data-testid="meals-by-type">
          {["colazione", "spuntino", "pranzo", "merenda", "cena"].map((mt) => {
            const items = foodLogs.filter((l) => l.meal_type === mt);
            const kcal = items.reduce((s, l) => s + (l.calories || 0), 0);
            const empty = items.length === 0;
            const isOpen = expandedMeals.has(mt);
            const label = ({
              colazione: t("diary.breakfast"),
              spuntino: t("diary.snack"),
              pranzo: t("diary.lunch"),
              merenda: t("diary.afternoon"),
              cena: t("diary.dinner"),
            }[mt]);
            const visual = ({
              colazione: { emoji: "🥐", bg: "#FFE8CC", ring: "#E8951C" },
              spuntino:  { emoji: "🍎", bg: "#FFE0E0", ring: "#EF5757" },
              pranzo:    { emoji: "🥗", bg: "#DEF0E4", ring: "#4CAF7C" },
              merenda:   { emoji: "🍪", bg: "#F1E4D2", ring: "#B08957" },
              cena:      { emoji: "🍽️", bg: "#DDE9EA", ring: "#2C5254" },
            }[mt]);
            return (
              <div
                key={mt}
                data-testid={`meal-row-${mt}`}
                className={`relative rounded-2xl border ${empty ? "border-dashed border-slate-200 bg-white/50" : "border-slate-100 bg-white shadow-sm"} transition`}
              >
                <div
                  role={empty ? undefined : "button"}
                  tabIndex={empty ? undefined : 0}
                  onClick={() => !empty && toggleMealGroup(mt)}
                  onKeyDown={(e) => { if (!empty && (e.key === "Enter" || e.key === " ")) toggleMealGroup(mt); }}
                  data-testid={`meal-row-${mt}-header`}
                  className={`w-full flex items-center gap-3 px-3 py-3 text-left ${empty ? "" : "cursor-pointer hover:bg-slate-50/60 active:bg-slate-50 transition"}`}
                >
                  <div
                    className="shrink-0 h-11 w-11 rounded-2xl flex items-center justify-center text-2xl leading-none"
                    style={{
                      background: empty ? "#F1F5F9" : visual.bg,
                      boxShadow: empty ? "none" : `inset 0 0 0 1px ${visual.ring}22`,
                      opacity: empty ? 0.5 : 1,
                    }}
                    aria-label={label}
                    title={label}
                  >
                    <span aria-hidden="true">{visual.emoji}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${empty ? "text-slate-400" : "text-[#1A1D1E]"}`}>{label}</p>
                    {empty ? (
                      <p className="text-xs text-slate-400 italic truncate">{t("cliDash.mealEmpty")}</p>
                    ) : (
                      <p className="text-xs text-slate-500 truncate" data-testid={`meal-row-${mt}-count`}>
                        {items.length === 1 ? t("cliDash.oneMeal") : `${items.length} ${t("cliDash.manyMeals")}`}
                      </p>
                    )}
                  </div>
                  {!empty && (
                    <>
                      <span className="text-sm font-semibold text-[#2C5254] shrink-0" data-testid={`meal-row-${mt}-kcal`}>{kcal} kcal</span>
                      <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </>
                  )}
                  <div onClick={(e) => e.stopPropagation()} className="shrink-0" data-testid={`meal-row-${mt}-quicktrack-wrap`}>
                    <QuickTrackMenu
                      onVoice={() => { voicePendingMealTypeRef.current = mt; voiceBtnRef.current?.beginRecording?.(); }}
                      date={date}
                      onSaved={() => loadDaily(date)}
                      mealType={mt}
                      size="sm"
                      testIdSuffix={`meal-${mt}`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setSearchDialog({ open: true, mealType: mt }); }}
                    data-testid={`meal-row-${mt}-add`}
                    aria-label={`add ${mt}`}
                    className="h-8 w-8 rounded-full bg-white border border-slate-200 hover:border-[#5A8B8F]/40 hover:bg-[#5A8B8F]/5 flex items-center justify-center transition active:scale-95 shrink-0"
                  >
                    <Plus className="h-3.5 w-3.5 text-[#5A8B8F]" />
                  </button>
                </div>
                {!empty && isOpen && (
                  <div
                    className="border-t border-slate-100/80 dark:border-white/5 divide-y divide-slate-100/50 dark:divide-white/[0.04] bg-slate-50/50 dark:bg-white/[0.03] rounded-b-2xl overflow-hidden"
                    data-testid={`meal-row-${mt}-items`}
                  >
                    {items.map((l) => {
                      const desc = l.description || "";
                      const qtyMatch = desc.match(/\s*·\s*([\d.,]+\s*(?:g|gr|kg|ml|l|stück|pz)\.?)\s*$/i);
                      const qty = qtyMatch ? qtyMatch[1].trim() : null;
                      const rest = qtyMatch ? desc.slice(0, qtyMatch.index).trim() : desc;
                      const parenMatch = rest.match(/^([^(]+?)\s*\(([^)]+)\)$/);
                      const main = parenMatch ? parenMatch[1].trim() : rest;
                      const sub = parenMatch ? parenMatch[2].trim() : null;
                      return (
                        <button
                          key={l.log_id}
                          onClick={() => navigate(`/app/pasto?id=${l.log_id}`)}
                          data-testid={`meal-item-${l.log_id}`}
                          className="w-full text-left px-4 py-2 flex items-center gap-3 hover:bg-slate-100/50 dark:hover:bg-white/5 active:bg-slate-100 transition"
                        >
                          {l.photo_base64 && (
                            <img src={l.photo_base64} alt="" className="h-8 w-8 rounded-md object-cover shrink-0" />
                          )}
                          <span className="flex-1 min-w-0">
                            <span className="flex items-baseline gap-1.5 min-w-0">
                              <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{main}</span>
                              {qty && <span className="text-[13px] text-slate-400 shrink-0 whitespace-nowrap">· {qty}</span>}
                            </span>
                            {sub && <span className="block text-[11px] text-slate-400 dark:text-slate-500 truncate">{sub}</span>}
                          </span>
                          {l.calories != null && (
                            <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0">{l.calories} kcal</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Aktivitäten section (Streak + Weight + Plan preview) — moved to Fortschritt page */}
      </div>

      {/* Kcal target edit dialog */}
      <Dialog open={kcalDialogOpen} onOpenChange={setKcalDialogOpen}>
        <DialogContent className="sm:max-w-sm rounded-3xl" data-testid="kcal-target-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{t("cliDash.editKcalTitle")}</DialogTitle>
            <DialogDescription className="sr-only">{t("cliDash.editKcalHint")}</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-slate-500 mb-3">{t("cliDash.editKcalHint")}</p>
            <div className="flex items-baseline gap-2">
              <Input
                type="number"
                inputMode="numeric"
                min={500}
                max={6000}
                value={newKcalTarget}
                onChange={(e) => setNewKcalTarget(e.target.value)}
                placeholder="2000"
                className="h-14 text-2xl font-heading font-semibold text-center flex-1"
                autoFocus
                data-testid="kcal-target-input"
                onKeyDown={(e) => { if (e.key === "Enter") saveKcalTarget(); }}
              />
              <span className="text-sm text-slate-500">kcal / {t("cliDash.today").toLowerCase()}</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {[1500, 1800, 2000, 2200, 2500, 2800, 3000].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setNewKcalTarget(String(v))}
                  data-testid={`kcal-quick-${v}`}
                  className={`px-3 h-8 rounded-full text-xs font-medium border transition ${
                    +newKcalTarget === v
                      ? "bg-[#5A8B8F] text-white border-[#5A8B8F]"
                      : "bg-white text-slate-600 border-slate-200 hover:border-[#5A8B8F]/40"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => { setKcalDialogOpen(false); setCalcOpen(true); }}
              data-testid="open-calculator-btn"
              className="mt-4 w-full rounded-2xl bg-gradient-to-r from-[#5A8B8F]/10 to-[#E8951C]/10 border border-slate-200 p-3 flex items-center gap-3 hover:from-[#5A8B8F]/20 hover:to-[#E8951C]/20 active:scale-[0.99] transition text-left"
            >
              <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm">
                <Calculator className="h-4 w-4 text-[#E8951C]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#2C5254]">{t("calc.ctaTitle")}</p>
                <p className="text-xs text-slate-500 truncate">{t("calc.ctaHint")}</p>
              </div>
              <span className="text-[#5A8B8F]">→</span>
            </button>
            <button
              type="button"
              onClick={() => { setKcalDialogOpen(false); setPlanOpen(true); }}
              data-testid="open-nutrition-plan-btn"
              className="mt-2 w-full rounded-2xl bg-white border border-slate-200 p-3 flex items-center gap-3 hover:border-[#5A8B8F]/50 active:scale-[0.99] transition text-left"
            >
              <div className="h-10 w-10 rounded-full bg-[#5A8B8F]/10 flex items-center justify-center shrink-0">
                <Target className="h-4 w-4 text-[#2C5254]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#2C5254]">{t("cliDash.planCtaTitle")}</p>
                <p className="text-xs text-slate-500 truncate">{t("cliDash.planCtaHint")}</p>
              </div>
              <span className="text-[#5A8B8F]">→</span>
            </button>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setKcalDialogOpen(false)} data-testid="kcal-cancel-btn"
              className="rounded-full">
              {t("common.cancel")}
            </Button>
            <Button onClick={saveKcalTarget} data-testid="kcal-save-btn"
              className="rounded-full bg-[#5A8B8F] hover:bg-[#4a7679] text-white">
              {t("cliDash.saveTarget")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Macro calculator dialog */}
      <MacroCalculatorDialog
        open={calcOpen}
        onOpenChange={setCalcOpen}
        onApplied={() => loadStatic()}
        onOpenPlan={() => { setCalcOpen(false); setPlanOpen(true); }}
        onManualEntry={(kcal) => {
          setCalcOpen(false);
          setNewKcalTarget(kcal ? String(kcal) : (kcalTarget ? String(kcalTarget) : ""));
          setKcalDialogOpen(true);
        }}
      />

      {/* Self-service targets & macro split (same dialog the coach uses) */}
      <NutritionPlanDialog open={planOpen} onOpenChange={setPlanOpen} selfMode onSaved={() => loadStatic()} />

      {/* Quick food log dialog — opens when the user taps + on any meal row */}
      <FoodSearchDialog
        open={searchDialog.open}
        onOpenChange={(o) => {
          setSearchDialog((s) => ({ ...s, open: o }));
          // Dialog closed WITHOUT logging while a multi-flow is active:
          // reopen the multi-sheet so the user can pick another item or retry.
          if (!o && multiSheet.pendingIdx != null) {
            setMultiSheet((s) => ({ ...s, open: true, pendingIdx: null }));
          }
        }}
        initialQuery={searchDialog.initialQuery}
        initialQuantityG={searchDialog.initialQuantityG}
        onSelect={async (payload) => {
          try {
            // Voice-triggered logs (mealType==null) get contextual bucketing.
            const voiceAssigned = !searchDialog.mealType;
            const mealType = searchDialog.mealType || defaultMealTypeForNow();
            const { data: created } = await postFoodLog({
              ...payload,
              date,
              meal_type: mealType,
            });
            const logId = created?.pending_sync ? null : created?.log_id;
            // Rich toast with a "Move to X" action when we auto-picked the bucket.
            if (voiceAssigned && logId) {
              const other = suggestOtherMeal(mealType);
              toast.success(
                `${t("diary.mealLogged")} · ${t(`mealType.${mealType}`)}`,
                {
                  action: {
                    label: t("voiceFood.moveTo").replace("{name}", t(`mealType.${other}`)),
                    onClick: async () => {
                      try {
                        await api.patch(`/client/food-log/${logId}/meal-type`, { meal_type: other });
                        toast.success(t("voiceFood.moved").replace("{name}", t(`mealType.${other}`)));
                        loadDaily(date);
                      } catch {
                        toast.error(t("common.error"));
                      }
                    },
                  },
                  duration: 6000,
                }
              );
            } else {
              toast.success(t("diary.mealLogged"));
            }
            setSearchDialog({ open: false, mealType: null, initialQuery: "", initialQuantityG: null });
            loadDaily(date);
            // If we're inside a voice multi-flow, mark the just-logged item as
            // done and reopen the sheet with remaining items.
            const idx = multiSheet.pendingIdx;
            if (idx != null) {
              setMultiSheet((s) => {
                const updated = s.items.map((x, i) => (i === idx ? { ...x, done: true } : x));
                const remaining = updated.filter((x) => !x.done).length;
                if (remaining === 0) {
                  toast.success(t("voiceFood.multiAllDone").replace("{n}", updated.length));
                  return { open: false, items: [], pendingIdx: null };
                }
                return { open: true, items: updated, pendingIdx: null };
              });
            }
          } catch {
            toast.error(t("common.error"));
          }
        }}
      />

      {/* Voice multi-food sheet: appears when the parser detects >1 items */}
      <VoiceMultiSheet
        open={multiSheet.open}
        onOpenChange={(o) => setMultiSheet((s) => ({ ...s, open: o }))}
        items={multiSheet.items}
        onPickItem={(it, idx) => {
          // Close sheet, remember which item is being logged, open dialog.
          setMultiSheet((s) => ({ ...s, open: false, pendingIdx: idx }));
          setSearchDialog({
            open: true,
            mealType: null,
            initialQuery: it.query,
            initialQuantityG: it.quantity_g || null,
          });
        }}
      />

      <OnboardingWizard open={onboardingOpen} onClose={() => setOnboardingOpen(false)} />
      <WaterMilestone2LOverlay show={waterMilestoneShow} onClose={() => setWaterMilestoneShow(false)} />
      <TrainingCompleteOverlay show={trainingShow} onClose={() => setTrainingShow(false)} />
      <StepsGoalOverlay show={stepsGoalShow} onClose={() => setStepsGoalShow(false)} steps={stepsToday} />
      <SleepGoalOverlay show={sleepGoalShow} onClose={() => setSleepGoalShow(false)} sleepMinutes={sleepMinToday} />

      {/* Hidden voice button — imperatively driven by QuickTrackMenu → "Stimme".
          Rendered off-screen so parents can call beginRecording() on the ref.
          Bug fix Feb 2026: previously the ref target wasn't mounted anywhere,
          so voiceBtnRef.current was always null and tapping "Stimme" did
          nothing (menu closed silently). */}
      <div className="sr-only" aria-hidden="true">
        <VoiceFoodButton
          ref={voiceBtnRef}
          onResult={({ query, quantity_g, pieces, items }) => {
            const mt = voicePendingMealTypeRef.current;
            voicePendingMealTypeRef.current = null;
            // Multiple foods detected → open the picker sheet so the user
            // can log them one by one.
            if (Array.isArray(items) && items.length > 1) {
              setMultiSheet({ open: true, items, pendingIdx: null });
              return;
            }
            // Single food → open the FoodSearchDialog pre-filled with the
            // transcribed query and grams (if any).
            setSearchDialog({
              open: true,
              mealType: mt,           // null → auto-bucket by time of day
              initialQuery: query || "",
              initialQuantityG: quantity_g || null,
            });
            if (pieces && pieces > 1) {
              // parseVoiceFood returns pieces like "3 mele" — hint the user
              // in the toast that the grams field is per-item.
              toast.info(`${pieces}× ${query}`);
            }
          }}
        />
      </div>
    </Layout>
  );
}

// Clickable avatar circle in the greeting. Tap → native file picker (camera or
// gallery on mobile). Auto-resizes to 256×256 JPEG so the base64 payload stays
// well under the 1.5 MB backend limit.
function AvatarUploader({ user, refreshUser, firstName, t }) {
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  // Editor state — when the user picks a file we open a full-screen modal
  // where they can zoom/pan the photo to centre exactly what they want and
  // pick one of six frame styles. The final composed image (already framed
  // by CSS on export doesn't work, so we save the frame choice as metadata
  // and let CSS render the ring on every consumer of the avatar).
  const [editorSrc, setEditorSrc] = React.useState(null);
  const [zoom, setZoom] = React.useState(1);
  const [offset, setOffset] = React.useState({ x: 0.5, y: 0.5 });
  const [frame, setFrame] = React.useState(user?.avatar_frame || "none");
  const editorImgRef = React.useRef(null);
  const editorCanvasRef = React.useRef(null);
  const dragRef = React.useRef(null);

  const pickFile = () => { setMenuOpen(false); fileRef.current?.click(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Read into a data URL and open the editor. Actual upload only happens
    // when the user hits Save inside the modal.
    const reader = new FileReader();
    reader.onload = () => {
      setEditorSrc(reader.result);
      setZoom(1);
      setOffset({ x: 0.5, y: 0.5 });
      setFrame(user?.avatar_frame || "none");
    };
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  // Build the cropped 256×256 JPEG based on current zoom/offset. The visible
  // circle in the editor covers 260px; we draw the source image with
  // `object-fit: cover` semantics at the chosen zoom and offset.
  const buildCropped = () => {
    return new Promise((resolve) => {
      const size = 256;
      const img = editorImgRef.current;
      if (!img) return resolve(null);
      const c = editorCanvasRef.current || document.createElement("canvas");
      c.width = size; c.height = size;
      const ctx = c.getContext("2d");
      ctx.clearRect(0, 0, size, size);
      // Fit-cover base rectangle: shortest side of source scaled to `size`.
      const s = Math.min(img.naturalWidth, img.naturalHeight);
      const baseSx = (img.naturalWidth - s) / 2;
      const baseSy = (img.naturalHeight - s) / 2;
      // At zoom Z, sample a smaller square of side s/Z, positioned by offset.
      const src = s / zoom;
      const maxOff = s - src;
      const sx = baseSx + maxOff * offset.x;
      const sy = baseSy + maxOff * offset.y;
      ctx.drawImage(img, sx, sy, src, src, 0, 0, size, size);
      resolve(c.toDataURL("image/jpeg", 0.85));
    });
  };

  const saveEditor = async () => {
    setUploading(true);
    try {
      const dataUrl = await buildCropped();
      await api.post("/auth/avatar", { picture: dataUrl, frame });
      await refreshUser();
      toast.success(t("cliDash.avatarUpdated"));
      setEditorSrc(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setUploading(false);
    }
  };

  // Change the frame on the existing picture without re-uploading — used
  // from the menu when the user just wants to swap the border.
  const applyFrame = async (f) => {
    setMenuOpen(false);
    try {
      await api.post("/auth/avatar", { frame: f });
      await refreshUser();
      toast.success(t("cliDash.avatarFrameUpdated") || t("cliDash.avatarUpdated"));
    } catch { toast.error(t("common.error")); }
  };

  const removeAvatar = async () => {
    setMenuOpen(false);
    try {
      await api.delete("/auth/avatar");
      await refreshUser();
      toast.success(t("cliDash.avatarRemoved"));
    } catch { toast.error(t("common.error")); }
  };

  // Drag-to-pan handlers — track pointer delta relative to editor circle.
  const startDrag = (e) => {
    const point = e.touches?.[0] || e;
    dragRef.current = { x: point.clientX, y: point.clientY, offX: offset.x, offY: offset.y };
  };
  const onDrag = (e) => {
    if (!dragRef.current) return;
    const point = e.touches?.[0] || e;
    const dx = (point.clientX - dragRef.current.x) / 260;
    const dy = (point.clientY - dragRef.current.y) / 260;
    // Larger zoom = finer pan range because more of the source is visible.
    setOffset({
      x: Math.max(0, Math.min(1, dragRef.current.offX - dx * zoom)),
      y: Math.max(0, Math.min(1, dragRef.current.offY - dy * zoom)),
    });
  };
  const endDrag = () => { dragRef.current = null; };

  // Frame CSS — applied consistently in the button ring and everywhere else
  // via the `frameRingClass` helper (also exported for other consumers).
  const currentFrame = user?.avatar_frame || "none";
  const frameRing = (f) => {
    switch (f) {
      case "teal":     return "ring-[3px] ring-[#5A8B8F]";
      case "amber":    return "ring-[3px] ring-[#E8951C]";
      case "gold":     return "ring-[3px] ring-yellow-400 shadow-[0_0_0_1px_rgba(202,138,4,0.6)]";
      case "double":   return "ring-4 ring-white shadow-[0_0_0_5px_#2C5254]";
      case "gradient": return "p-0 [background:conic-gradient(from_180deg,#E8951C,#5A8B8F,#2C5254,#E8951C)] rounded-full";
      default:         return "ring-2 ring-white";
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => (user?.picture ? setMenuOpen((v) => !v) : pickFile())}
        disabled={uploading}
        data-testid="avatar-btn"
        aria-label={t("cliDash.changePhoto")}
        className={`relative group h-11 w-11 rounded-full shadow-sm overflow-hidden hover:scale-105 transition ${frameRing(currentFrame)}`}
      >
        {currentFrame === "gradient" && (
          <span className="absolute inset-[3px] rounded-full bg-white" aria-hidden />
        )}
        {user?.picture ? (
          <img src={user.picture} alt="" className={`relative h-full w-full rounded-full object-cover ${currentFrame === "gradient" ? "inset-[3px] h-[calc(100%-6px)] w-[calc(100%-6px)] absolute" : ""}`} />
        ) : (
          <div className="h-11 w-11 rounded-full bg-[#5A8B8F]/15 text-[#2C5254] flex items-center justify-center text-base font-semibold">
            {firstName[0] || "U"}
          </div>
        )}
        <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-[#5A8B8F] text-white flex items-center justify-center border-2 border-white">
          <Camera className="h-2.5 w-2.5" />
        </span>
      </button>

      {menuOpen && user?.picture && (
        <div
          className="absolute z-30 top-12 left-0 w-56 rounded-2xl bg-white border border-slate-200 shadow-lg overflow-hidden"
          data-testid="avatar-menu"
        >
          <button
            onClick={pickFile}
            className="w-full text-left px-4 py-3 text-sm hover:bg-slate-50 flex items-center gap-2"
          >
            <Camera className="h-4 w-4 text-[#5A8B8F]" /> {t("cliDash.changePhoto")}
          </button>
          <div className="px-4 py-2 border-t border-slate-100">
            <p className="text-[10px] uppercase tracking-wider text-[#5A8B8F] font-semibold mb-1.5">{t("cliDash.avatarFrame") || "Cornice"}</p>
            <div className="grid grid-cols-6 gap-1.5" data-testid="avatar-frame-picker">
              {["none","teal","amber","gold","double","gradient"].map((f) => (
                <button
                  key={f}
                  onClick={() => applyFrame(f)}
                  data-testid={`avatar-frame-${f}`}
                  aria-label={f}
                  className={`h-7 w-7 rounded-full flex items-center justify-center transition ${f === currentFrame ? "scale-110" : "hover:scale-105"} ${frameRing(f)} ${f === "gradient" ? "" : "bg-slate-100"}`}
                >
                  {f === "gradient" && <span className="h-4 w-4 rounded-full bg-white" />}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={removeAvatar}
            className="w-full text-left px-4 py-3 text-sm hover:bg-red-50 text-red-600 border-t border-slate-100"
          >
            {t("cliDash.removePhoto")}
          </button>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onFile}
        className="hidden"
        data-testid="avatar-input"
      />

      {/* Full-screen editor — appears after picking a file. Users drag to
          pan, use the slider to zoom, and pick a frame style. */}
      {editorSrc && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="avatar-editor">
          <div className="w-full max-w-sm bg-[#E5F0F1] rounded-3xl p-5 shadow-xl relative">
            <button
              onClick={() => setEditorSrc(null)}
              data-testid="avatar-editor-close"
              className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/70 hover:bg-white flex items-center justify-center"
              aria-label="close"
            >
              <X className="h-4 w-4 text-slate-600" />
            </button>
            <p className="font-heading text-lg font-semibold text-[#2C5254] text-center mb-4">
              {t("cliDash.editAvatar") || "Foto zentrieren"}
            </p>
            <div
              className={`mx-auto h-[260px] w-[260px] rounded-full overflow-hidden bg-slate-900 relative shadow-inner cursor-grab active:cursor-grabbing ${frameRing(frame)}`}
              onMouseDown={startDrag}
              onMouseMove={onDrag}
              onMouseUp={endDrag}
              onMouseLeave={endDrag}
              onTouchStart={startDrag}
              onTouchMove={onDrag}
              onTouchEnd={endDrag}
              data-testid="avatar-editor-canvas-wrap"
            >
              {frame === "gradient" && <span className="absolute inset-[3px] rounded-full bg-black z-0" />}
              <img
                ref={editorImgRef}
                src={editorSrc}
                alt=""
                draggable={false}
                className="absolute select-none pointer-events-none"
                style={{
                  width: `${zoom * 100}%`,
                  height: `${zoom * 100}%`,
                  objectFit: "cover",
                  left: `${(1 - zoom) * 100 * offset.x}%`,
                  top: `${(1 - zoom) * 100 * offset.y}%`,
                  ...(zoom > 1 ? { transform: `translate(${-(zoom - 1) * 100 * offset.x}%, ${-(zoom - 1) * 100 * offset.y}%)` } : {}),
                }}
                onLoad={() => { setZoom(1); setOffset({ x: 0.5, y: 0.5 }); }}
              />
            </div>
            <canvas ref={editorCanvasRef} className="hidden" />

            <div className="mt-4">
              <label className="text-[11px] uppercase tracking-wider text-[#5A8B8F] font-semibold">
                {t("cliDash.avatarZoom") || "Zoom"}
              </label>
              <input
                type="range"
                min="1" max="3" step="0.05"
                value={zoom}
                onChange={(e) => setZoom(+e.target.value)}
                className="w-full accent-[#5A8B8F] mt-1"
                data-testid="avatar-zoom"
              />
            </div>

            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider text-[#5A8B8F] font-semibold mb-2">
                {t("cliDash.avatarFrame") || "Cornice"}
              </p>
              <div className="grid grid-cols-6 gap-2" data-testid="editor-frame-picker">
                {["none","teal","amber","gold","double","gradient"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFrame(f)}
                    data-testid={`editor-frame-${f}`}
                    aria-label={f}
                    className={`h-9 w-9 mx-auto rounded-full flex items-center justify-center transition ${f === frame ? "scale-110" : "hover:scale-105"} ${frameRing(f)} ${f === "gradient" ? "" : "bg-white"}`}
                  >
                    {f === "gradient" && <span className="h-4 w-4 rounded-full bg-white" />}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={saveEditor}
              disabled={uploading}
              data-testid="avatar-editor-save"
              className="mt-5 w-full h-12 rounded-full bg-[#5A8B8F] hover:bg-[#4a7679] text-white font-semibold shadow-md disabled:opacity-60"
            >
              {uploading ? (t("common.saving") || "…") : (t("common.save") || "Speichern")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

